import { useState } from 'react';

const packages = [
  {
    vehicle: '2026 BMW 740i',
    color: 'Midnight Blue',
    options: [
      { name: 'Drop-Off Only', price: 1000, deposit: 500, remaining: 500 },
      { name: 'Round Trip', price: 1200, deposit: 500, remaining: 700 },
    ],
  },
  {
    vehicle: '2026 BMW 530i',
    color: 'Pearl White',
    options: [
      { name: 'Drop-Off Only', price: 850, deposit: 500, remaining: 350 },
      { name: 'Round Trip', price: 1000, deposit: 500, remaining: 500 },
    ],
  },
];

export default function PaymentSection() {
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');

  const handlePayClick = (label: string) => {
    setSelectedPackage(label);
    setShowModal(true);
  };

  return (
    <section id="payment" className="py-28 bg-[#050505] relative">
      <div className="max-w-5xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <p className="text-gray-500 text-xs tracking-[0.35em] uppercase mb-4">Investment</p>
          <h2 className="font-display silver-gradient-text text-5xl md:text-6xl font-semibold tracking-wide mb-6">
            Pricing &amp; Payment
          </h2>
          <p className="text-gray-400 text-sm tracking-wide leading-relaxed max-w-xl mx-auto">
            Transparent pricing for your luxury Philadelphia prom chauffeur experience. All packages include a professional chauffeur.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {packages.map((pkg) => (
            <div key={pkg.vehicle} className="card-luxury rounded-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-black to-gray-900 border-b border-gray-800 p-6">
                <h3 className="font-display silver-gradient-text text-2xl font-semibold mb-1">{pkg.vehicle}</h3>
                <p className="text-gray-500 text-xs tracking-[0.15em] uppercase">{pkg.color}</p>
              </div>

              {/* Options */}
              <div className="p-6 space-y-4">
                {pkg.options.map((opt) => (
                  <div key={opt.name} className="bg-black/40 border border-gray-800 rounded-xl p-5 hover:border-gray-600 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-300 text-xs tracking-[0.15em] uppercase font-medium">{opt.name}</span>
                      <span className="font-display silver-gradient-text text-2xl font-semibold">${opt.price.toLocaleString()}</span>
                    </div>
                    {/* Payment breakdown */}
                    <div className="space-y-2 mb-5">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-xs">Deposit (non-refundable)</span>
                        <span className="text-gray-300 text-xs font-medium">${opt.deposit}</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-px"></div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-xs">Remaining balance</span>
                        <span className="text-gray-300 text-xs font-medium">${opt.remaining}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handlePayClick(`${pkg.vehicle} — ${opt.name}`)}
                      className="w-full btn-silver rounded-lg py-3 text-xs tracking-[0.15em] uppercase cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-secure-payment-line text-sm"></i>
                      </div>
                      Pay $500 Deposit
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stripe Badge */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 bg-[#0d0d0d] border border-gray-800 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center silver-border rounded-full shrink-0">
              <i className="ri-lock-line text-gray-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-sm font-medium">Secure Stripe Payment</p>
              <p className="text-gray-500 text-xs">256-bit SSL encryption</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-gray-800"></div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center silver-border rounded-full shrink-0">
              <i className="ri-bank-card-line text-gray-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-sm font-medium">All Major Cards Accepted</p>
              <p className="text-gray-500 text-xs">Visa, Mastercard, Amex</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-gray-800"></div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center silver-border rounded-full shrink-0">
              <i className="ri-customer-service-2-line text-gray-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-sm font-medium">Questions?</p>
              <a href="tel:2677731129" className="text-gray-400 text-xs hover:text-gray-300 cursor-pointer">(267) 773-1129</a>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="card-luxury rounded-2xl p-8 max-w-md w-full text-center relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-300 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="w-14 h-14 flex items-center justify-center silver-border rounded-full mx-auto mb-5">
              <i className="ri-secure-payment-line text-xl text-gray-300"></i>
            </div>
            <h3 className="font-display silver-gradient-text text-2xl font-semibold mb-2">
              Almost There!
            </h3>
            <p className="text-gray-400 text-sm mb-2">{selectedPackage}</p>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Secure online Stripe payment is being set up. In the meantime, please submit a booking request and we'll send you a direct payment link for your <strong className="text-gray-300">$500 deposit</strong>.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-silver rounded-xl py-3 text-xs tracking-[0.15em] uppercase cursor-pointer whitespace-nowrap"
              >
                Submit Booking Request
              </button>
              <a
                href="tel:2677731129"
                className="btn-outline-silver rounded-xl py-3 text-xs tracking-[0.15em] uppercase cursor-pointer whitespace-nowrap block text-center"
              >
                Call (267) 773-1129
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="section-divider mt-20"></div>
    </section>
  );
}
